import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ReactComponent as ArrowIcon } from "../assets/arrow.svg";
import Button from "../components/Button";
import ChatBlock from "../components/ChatBlock";
import { handleRedirect, sendAnalyticsClickEvent } from "../../../utils/CommonFunctions";

import { useRecoEngine } from "../context/RecoEngineContext";
import useRefineForm from "./useRefineForm";
import useRecoEngineSession from "./useRecoEngineSession";

const useRecoEngineActions = () => {
    const {
        setRenderBlocks,
        setFeedbackRow,
        setReadyForQuery,
        setQuestionsObject,
        setRefineForm,
        setLoading,
        setRefineFormCount,
        refineFormCount,
    } = useRecoEngine();

    const { fetchQuestions } = useRefineForm({});

    const {
        addBlock,
    } = useRecoEngineSession();

    const history = useNavigate();
    const [t] = useTranslation("translations");

    const executeAction = useCallback((
        action: "new_query" | "end_query" | "refine_query" | "send_message",
        data: {
            userMessage?: string;
            engineMessage?: string;
            additionalBlocks?: React.ReactElement[];
            analyticsEvent?: string;
            sessionData?: any[]
        }
    ) => {
        const { 
            userMessage, 
            engineMessage, 
            additionalBlocks = [], 
            analyticsEvent, 
            sessionData
        } = data;
        sendAnalyticsClickEvent(analyticsEvent, "RECO_ENGINE");

        const newBlocks = [];

        if (userMessage) {
            newBlocks.push(<ChatBlock key={`chat-user-${Date.now()}`} text={userMessage} />);
        }
        if (engineMessage) {
            newBlocks.push(<ChatBlock key={`chat-engine-${Date.now()}`} text={engineMessage} engineResponse />);
        }
        if (additionalBlocks.length > 0) {
            newBlocks.push(...additionalBlocks);
        }
        setRenderBlocks((prevBlocks) => [...prevBlocks, ...newBlocks]);

        if (sessionData) {
            sessionData.forEach((block)=> {
                addBlock(block)
            })
        }

        return newBlocks;
    }, [setRenderBlocks, addBlock])


    const newQuery = useCallback(() => {
        executeAction("new_query", {
            userMessage: t("RecoSearch.new_query_user"),
            engineMessage: t("RecoSearch.new_query_engine"),
            analyticsEvent: "New Query",
            sessionData: [{
                type: "chat",
                data: {
                    text: t("RecoSearch.new_query_user")
                }
            }]
        })
        setFeedbackRow(false);
        setReadyForQuery(true);
        setRefineFormCount(0);
    }, [executeAction, setFeedbackRow, setReadyForQuery])

    const endQuery = useCallback(() => {
        executeAction("end_query", {
            userMessage: t("RecoSearch.end_query_user"),
            engineMessage: t("RecoSearch.end_query_engine"),
            additionalBlocks: [
                <Button 
                    key={`button-${Date.now()}`} 
                    onClick={() => handleRedirect({type: "IN_APP_BROWSER", url: "https://www.google.com"}, history, "Provide Feedback", "RECO_ENGINE")} 
                    className="action-button" 
                    icon={<ArrowIcon />}
                    text={t("RecoSearch.provide_feedback")} 
                />
            ],
            analyticsEvent: "End Query",
            sessionData: [
                {
                    type: "chat",
                    data: {
                        text: t("RecoSearch.end_query_user")
                    }
                }
            ]
        })
        setFeedbackRow(false);
        setRefineFormCount(0);
        setReadyForQuery(true);
    }, [executeAction])

    const refineQuery = useCallback(() => {
        setLoading(true)
        setQuestionsObject({
            questions: [],
            answers: []
        })

        fetchQuestions();

        const currentCount = refineFormCount + 1
        setRefineFormCount(currentCount)

        // If refineFormCount is greater than 2, show the refine form third message
        if (currentCount > 2) {
            executeAction("send_message", {
                userMessage: t("RecoSearch.refine_query_user"),
                engineMessage: t("RecoSearch.refine_form_third_message"),
                analyticsEvent: "Refine Form Third Message",
            })
            executeAction("refine_query", {
                engineMessage: t("RecoSearch.refine_query_engine"),
                analyticsEvent: "Refine Query",
                sessionData: [
                        { 
                        type: "chat", 
                        data: { text: t("RecoSearch.refine_query_user") } 
                    },
                ]
            })
        } else {
            executeAction("refine_query", {
                userMessage: t("RecoSearch.refine_query_user"),
                engineMessage: t("RecoSearch.refine_query_engine"),
                analyticsEvent: "Refine Query",
                sessionData: [
                        { 
                        type: "chat", 
                        data: { text: t("RecoSearch.refine_query_user") } 
                    },
                ]
            })
        }
        
        setRefineForm(true);
        setFeedbackRow(false);
        setReadyForQuery(false);
    }, [executeAction, refineFormCount])

    return {
        newQuery,
        endQuery,
        refineQuery,
        executeAction
      };
}

export default useRecoEngineActions;
